import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Image, 
  MapPin, 
  Heart, 
  Users, 
  Bell, 
  Settings, 
  LogOut,
  Menu,
  X,
  Stethoscope,
  Sparkles
} from 'lucide-react';
import { Avatar, Button } from './ui';
import LanguageSelector from './LanguageSelector';

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const isArtist = user?.role === 'artist';
  const isAdmin = user?.role === 'admin' || user?.is_admin;

  const userNavItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/collection', label: t('nav.collection'), icon: Image },
    { path: '/smart-check', label: t('nav.smartCheck'), icon: Stethoscope, highlight: true },
    { path: '/body-map', label: t('nav.bodyMap'), icon: MapPin },
    { path: '/care', label: t('nav.care'), icon: Heart },
    { path: '/artists', label: t('nav.artists'), icon: Users },
    { path: '/ar-preview', label: t('nav.arPreview'), icon: Sparkles },
  ];

  const artistNavItems = [
    { path: '/artist/dashboard', label: t('nav.artistDashboard', 'Dashboard'), icon: LayoutDashboard },
    { path: '/artists', label: t('nav.artists'), icon: Users },
  ];

  const navItems = isArtist ? artistNavItems : userNavItems;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="glass sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={user ? (isArtist ? '/artist/dashboard' : '/dashboard') : '/'} className="flex items-center gap-2">
            <span className="font-heading text-2xl tracking-tight">
              INK<span className="text-primary">TRACK</span>
            </span>
            {isArtist && <span className="text-[10px] font-heading text-primary/60 -ml-1">ARTIST</span>}
          </Link>

          {/* Desktop Navigation */}
          {user && (
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      isActive 
                        ? 'text-primary' 
                        : item.highlight
                          ? 'text-secondary hover:text-primary'
                          : 'text-muted-foreground hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden lg:inline">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          )}

          {/* User Menu */}
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Link to="/admin" className="text-xs font-heading text-muted-foreground hover:text-primary transition-colors px-2 py-1 border border-border rounded-sm">
                Admin
              </Link>
            )}
            <LanguageSelector variant="navbar" />
            {user ? (
              <>
                <Link to="/settings" className="hidden sm:block">
                  <Avatar
                    src={user.picture}
                    alt={user.name}
                    fallback={user.name?.charAt(0)}
                    size="sm"
                  />
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  className="hidden sm:flex"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
                <button
                  className="md:hidden p-2"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  data-testid="mobile-menu-toggle"
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="login-nav-btn">
                    {t('nav.login', 'Sign In')}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="sm" data-testid="start-nav-btn">
                    {t('nav.getStarted', 'Get Started')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {user && mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden border-t border-border bg-background-secondary"
        >
          <div className="px-4 py-4 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    isActive 
                      ? 'bg-primary/10 text-primary' 
                      : 'text-muted-foreground hover:bg-muted hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
            <div className="pt-4 border-t border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Avatar
                  src={user.picture}
                  alt={user.name}
                  fallback={user.name?.charAt(0)}
                  size="sm"
                />
                <span className="text-sm">{user.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('nav.logout', 'Sign Out')}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;
